import { ref } from 'vue'
import { useGlobalState } from './useGlobalState.js'

export function useUrssafApi() {
  const { getAuthHeader } = useGlobalState()
  const isEstimatingTax = ref(false)
  const estimatedTax = ref<number | null>(null)
  const estimationError = ref<string | null>(null)

  /**
   * Estime l'impôt sur le revenu via l'API du serveur (Symfony Strategy).
   * @param rawIncome Le revenu brut total avant abattement de 10%
   * @param year L'année fiscale concernée
   * @returns Le montant estimé de l'impôt
   */
  async function estimateIncomeTax(rawIncome: number, year: number): Promise<number | null> {
    if (rawIncome <= 0) {
      estimatedTax.value = 0
      estimationError.value = null
      return 0
    }

    isEstimatingTax.value = true
    estimationError.value = null
    estimatedTax.value = null

    try {
      const res = await fetch('/api/budget/calculate-taxes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ year, rawIncome, tnsRemuneration: 0 }),
      })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }

      const data = await res.json() as { incomeTax: number; urssaf: number }
      const roundedTax = Math.round(data.incomeTax)
      estimatedTax.value = roundedTax
      return roundedTax
    } catch (e: any) {
      estimationError.value = "Impossible d'estimer l'impôt"
      console.error(e)
      return null
    } finally {
      isEstimatingTax.value = false
    }
  }

  return {
    isEstimatingTax,
    estimatedTax,
    estimationError,
    estimateIncomeTax
  }
}

/**
 * Synchronous version of income tax calculation for formula evaluations.
 * Uses dynamic tax brackets passed from the backend strategies.
 */
export function calculateIncomeTaxSync(rawIncome: number, year: number, taxStrategies: Record<number, any>): number {
  if (rawIncome <= 0) return 0

  const strategy = taxStrategies[year]
  
  // Use strategy.incomeTax parameters to calculate, fallback to default 2024 if not loaded yet
  const incomeTaxParams = strategy?.incomeTax ?? {
    deductionRate: 0.10,
    deductionMin: 504,
    deductionMax: 14426,
    brackets: [
      { min: 0, max: 11497, rate: 0 },
      { min: 11497, max: 29315, rate: 0.11 },
      { min: 29315, max: 83823, rate: 0.30 },
      { min: 83823, max: 180294, rate: 0.41 },
      { min: 180294, max: null, rate: 0.45 }
    ]
  }

  const { deductionRate, deductionMin, deductionMax, brackets } = incomeTaxParams

  let deduction = rawIncome * deductionRate
  if (deduction > deductionMax) deduction = deductionMax
  if (deduction < deductionMin) deduction = Math.min(rawIncome, deductionMin)
  
  const revenuNetImposable = Math.max(0, rawIncome - deduction)

  let impotTotal = 0

  for (const tranche of brackets) {
    if (revenuNetImposable > tranche.min) {
      const maxVal = tranche.max === null ? Infinity : tranche.max
      const taxableInTranche = Math.min(revenuNetImposable, maxVal) - tranche.min
      impotTotal += taxableInTranche * tranche.rate
    }
  }

  return Math.round(impotTotal)
}

/**
 * Synchronous version of corporate tax (IS) calculation for formula evaluations.
 * Uses dynamic tax brackets passed from the backend strategies.
 */
export function calculateCorporateTaxSync(profit: number, year: number, taxStrategies: Record<number, any>): number {
  if (profit <= 0) return 0

  const strategy = taxStrategies[year]
  
  const corporateTaxParams = strategy?.corporateTax ?? {
    brackets: [
      { min: 0, max: 42500, rate: 0.15 },
      { min: 42500, max: null, rate: 0.25 }
    ]
  }

  const { brackets } = corporateTaxParams
  let impotTotal = 0

  for (const tranche of brackets) {
    if (profit > tranche.min) {
      const maxVal = tranche.max === null ? Infinity : tranche.max
      const taxableInTranche = Math.min(profit, maxVal) - tranche.min
      impotTotal += taxableInTranche * tranche.rate
    }
  }

  return Math.round(impotTotal)
}
